package com.evoapps.evoshift.domain.model

/**
 * A manager's post to the team of a workplace (SH-118): `workplaces/{workplaceId}/announcements/{id}`.
 * To everyone working there, or to one role ([targetRole]); pinned posts stay on top. Each post
 * lands in its recipients' notification inbox with a push (functions/announcements.js) and is
 * deleted after 12 months (TTL). Employees cannot post: there is no chat in v1.
 */
data class Announcement(
    val id: String,
    val workplaceId: String,
    val authorId: String,
    /** The author's display name when posting; null once they deleted their account. */
    val authorName: String?,
    val text: String,
    val pinned: Boolean,
    /** Only people of this role get it; null: everyone working there. */
    val targetRole: String?,
    /** ISO-8601 instant. */
    val createdAt: String,
    /** How many people it went to (written by the server once it is sent); null until then. */
    val recipientCount: Int? = null
) {
    /** Whether a person of [role] gets the post (managers see every post anyway). */
    fun isFor(role: String?): Boolean =
        targetRole.isNullOrBlank() || targetRole.trim().equals(role?.trim(), ignoreCase = true)

    companion object {
        /** Longest text (firestore.rules). */
        const val MAX_TEXT = 2000

        /** Longest role name (firestore.rules). */
        const val MAX_ROLE = 50
    }
}

/** That [userId] read a post, and when (ISO-8601): its read receipt, seen by the managers. */
data class AnnouncementRead(val userId: String, val readAt: String)

/**
 * Who of the people a post is for read it (the managers' read receipts, SH-118): [read] with
 * the time, newest first, and [notYet], by name.
 */
data class ReadReceipts(
    val read: List<Pair<User, String>>,
    val notYet: List<User>
) {
    val total: Int get() = read.size + notYet.size

    companion object {
        /**
         * The receipts of [announcement] among [team] (the workplace's active people): its
         * recipients are those of its role, without its author; [reads] by anyone else (e.g. a
         * manager who reads every post) count too.
         */
        fun of(announcement: Announcement, team: List<User>, reads: List<AnnouncementRead>): ReadReceipts {
            val readAt = reads.associate { it.userId to it.readAt }
            val people = team.filter { it.uid != announcement.authorId }
            val recipients = people.filter { person ->
                announcement.isFor(person.engagements[announcement.workplaceId]?.role) || person.uid in readAt
            }
            return ReadReceipts(
                read = recipients.mapNotNull { person -> readAt[person.uid]?.let { person to it } }
                    .sortedByDescending { it.second },
                notYet = recipients.filter { it.uid !in readAt }.sortedBy { it.displayName.lowercase() }
            )
        }
    }
}

/** The feed's order: pinned posts first, then the newest. */
fun List<Announcement>.feedOrder(): List<Announcement> =
    sortedWith(compareByDescending<Announcement> { it.pinned }.thenByDescending { it.createdAt })

/**
 * A manager's note on a shift for the person working it (SH-118):
 * `workplaces/{workplaceId}/shift_notes/{shiftId}`. Only that person and the managers read it
 * (colleagues see the shift, not the note). Deleted with the shift, or 12 months after its last
 * change (TTL).
 */
data class ShiftNote(
    val shiftId: String,
    val workplaceId: String,
    /** Who works the shift (the note is for them). */
    val userId: String,
    val note: String,
    val updatedAt: String,
    val updatedBy: String
) {
    companion object {
        /** Longest note (firestore.rules). */
        const val MAX_NOTE = 1000
    }
}
